import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  Alert,
  Avatar,
  Button,
  DatePicker,
  Input,
  Pagination,
  Result,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  notification,
} from 'antd';
import { DeleteOutlined, EditOutlined, EyeOutlined, SwapOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/lib/table';
import { refreshPendingCounts } from 'layouts/pending-counts';
import NoteActionModal, { NoteActionConfig } from 'pages/star-shared/components/note-action-modal';
import { NoWrap } from 'pages/star-shared/components/status-tags';
import { imageUrl } from 'pages/star-shared/utils';
import { initials } from 'utils/initials';
import type { Paged, StudioListItem, StudioSummary } from '../contract';
import {
  STUDIO_PAGE_LIMIT,
  STUDIO_PAGES,
  StudioSort,
  StudioViewer,
  deleteStudio,
  listStudios,
  useStudioLoad,
} from '../api';
import { describeStudioError, hasStudioErrorCode, mapStudioError } from '../studio-error';
import {
  DELETE_STUDIO_TEXT,
  EMPTY_TEXT,
  MANAGER_CHANGE_WARNING,
  MANAGER_INVALID_TAG,
  MANAGER_INVALID_TEXT,
  NO_MANAGER_TEXT,
  REASON_MIN,
  STUDIO_SORT_OPTIONS,
  creatorCountTooltip,
  managerInvalidWarning,
} from '../labels';
import { asOfLabel, formatKc, monthLabel, monthNumber, utcMonth } from '../format';
import CountPill from '../components/count-pill';
import DeltaCell from '../components/delta-cell';
import StudioFormModal from './studio-form-modal';
import ChangeManagerModal from './change-manager-modal';
import styles from '../styles.module.scss';

// ------------------------------------------------------ dùng chung cho các tab ---

/**
 * Lỗi tải dữ liệu của một tab trang Studio (bảng trạng thái mục 8.7): 409 STUDIO_SCOPE / STUDIO_NOT_MANAGER /
 * STUDIO_ROLE_NOT_ALLOWED → Result 403; 503 STUDIO_UNAVAILABLE → Result warning; còn lại → Alert + "Thử lại".
 */
export const StudioErrorBlock = ({ error, onRetry }: { error: any; onRetry?: () => void }) => {
  const view = describeStudioError(error);
  if (view.kind === 'unavailable') return <Result status="warning" title={view.message} />;
  if (view.kind === 'forbidden') {
    return (
      <Result
        status="403"
        title={view.message}
        extra={
          <Link to="/">
            <Button>Về Tổng quan</Button>
          </Link>
        }
      />
    );
  }
  return (
    <Alert
      type="error"
      showIcon
      message={view.message}
      action={
        onRetry ? (
          <Button size="small" onClick={onRetry}>
            Thử lại
          </Button>
        ) : undefined
      }
    />
  );
};

/**
 * Không tải được danh sách Studio cho bộ lọc (tab Đề nghị / Giám sát): báo cạnh bộ lọc kèm "Thử lại", thay vì
 * để ô "Studio" (và gợi ý "Người thao tác") rỗng không lời giải thích.
 */
export const StudioFilterError = ({ error, onRetry }: { error: any; onRetry?: () => void }) => (
  <Alert
    type="warning"
    showIcon
    message={`Không tải được danh sách Studio cho bộ lọc: ${mapStudioError(error)}`}
    action={
      onRetry ? (
        <Button size="small" onClick={onRetry}>
          Thử lại
        </Button>
      ) : undefined
    }
  />
);

/**
 * Trang đang xem hết dòng (vd. vừa xoá / duyệt / khép dòng cuối của trang cuối): lùi về trang cuối còn dữ
 * liệu. Bảng chỉ hiện thanh phân trang khi tổng > một trang, nên không lùi thì kẹt ở trang rỗng.
 */
export function useClampPage(
  page: number,
  setPage: (page: number) => void,
  data: Paged<unknown> | null,
  loading: boolean,
  limit: number = STUDIO_PAGE_LIMIT
): void {
  const rows = data ? (data.data || []).length : null;
  const total = data?.metadata?.total ?? 0;
  useEffect(() => {
    if (loading || rows === null || rows > 0 || page <= 1) return;
    const last = Math.max(1, Math.ceil(total / limit));
    if (last < page) setPage(last);
  }, [loading, rows, total, page, limit, setPage]);
}

/** Tên Studio để hiện (tiêu đề có thể null ở StudioRef). */
export const studioTitle = (s: { id: number; title: string | null }) => s.title || `Studio #${s.id}`;

// ------------------------------------------------------------------ tab ---

export interface StudioListTabProps {
  viewer: StudioViewer;
  /** Đổi giá trị để tải lại bảng (vd. vừa tạo Studio ở nút đầu trang). */
  reloadKey?: number;
  /** Sau khi sửa / đổi Quản lý / xoá một Studio (trang tải lại số đếm và danh sách Studio của bộ lọc). */
  onChanged?: () => void;
}

/**
 * Tab "Danh sách Studio" (`/studios`, mục 8.7.1; S2): tên, Quản lý, số Creator, số Nhóm nhảy, KC tháng này,
 * KC tháng trước, thay đổi CÙNG SỐ NGÀY (`kc.sameDay`, không bao giờ so tháng đang chạy với cả tháng trước),
 * đề nghị chờ, cờ mở. Admin: Sửa, Đổi Quản lý, Xoá. SMOD chỉ xem.
 */
const StudioListTab = ({ viewer, reloadKey = 0, onChanged }: StudioListTabProps) => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [month, setMonth] = useState<string | undefined>(undefined);
  const [sort, setSort] = useState<StudioSort>('kc_desc');
  // Studio của modal giữ nguyên trong lúc modal đang đóng (hiệu ứng đóng), chỉ `open` đổi.
  const [modal, setModal] = useState<{ kind: 'edit' | 'manager'; studio: StudioSummary } | null>(
    null
  );
  const [modalOpen, setModalOpen] = useState(false);
  // Mỗi lượt mở một `key` mới: modal (và form bên trong) dựng lại từ đầu, không mang giá trị của lượt trước —
  // kể cả khi mở Studio khác lúc hiệu ứng đóng chưa xong.
  const [modalSeq, setModalSeq] = useState(0);
  const openModal = (kind: 'edit' | 'manager', studio: StudioSummary) => {
    setModal({ kind, studio });
    setModalSeq((n) => n + 1);
    setModalOpen(true);
  };
  const [deleting, setDeleting] = useState<NoteActionConfig | null>(null);

  const list = useStudioLoad(
    () => listStudios({ page, limit: STUDIO_PAGE_LIMIT, q: q || undefined, month, sort }),
    [page, q, month, sort, reloadKey]
  );
  const rows = list.data?.data || [];
  const total = list.data?.metadata?.total ?? 0;
  const suggestedMonth = list.data?.suggestedMonth ?? null;
  useClampPage(page, setPage, list.data, list.loading);

  // Tháng mặc định = `suggestedMonth` do API trả (tháng gần nhất có dữ liệu). Lần đầu gọi không kèm tháng
  // (máy chủ dùng tháng UTC hiện tại); tháng đó chưa có dữ liệu thì chuyển sang tháng API gợi ý.
  useEffect(() => {
    if (month || !suggestedMonth) return;
    if (suggestedMonth !== utcMonth()) setMonth(suggestedMonth);
  }, [month, suggestedMonth]);
  const shownMonth = month || suggestedMonth || utcMonth();

  const afterChange = () => {
    list.reload();
    onChanged?.();
  };

  const scopeWarning = viewer.scopeMode ? MANAGER_CHANGE_WARNING[viewer.scopeMode] : null;

  const openDelete = (studio: StudioListItem) => {
    const base = (
      <div>
        <p style={{ marginTop: 0 }}>
          <strong>{studio.title}</strong>
        </p>
        <p>{DELETE_STUDIO_TEXT}</p>
        {studio.manager && scopeWarning && <Alert type="warning" showIcon message={scopeWarning} />}
      </div>
    );
    const config: NoteActionConfig = {
      title: 'Xoá Studio',
      description: base,
      noteRequired: true,
      noteLabel: `Lý do xoá (ít nhất ${REASON_MIN} ký tự)`,
      confirmText: 'Xoá Studio',
      danger: true,
      onSubmit: async (note) => {
        if ((note || '').length < REASON_MIN) {
          notification.error({ message: `Lý do cần ít nhất ${REASON_MIN} ký tự.` });
          throw new Error('REASON_TOO_SHORT');
        }
        try {
          await deleteStudio(studio.id, note as string);
          notification.success({ message: `Đã xoá Studio "${studio.title}".` });
          refreshPendingCounts();
          afterChange();
        } catch (err) {
          if (hasStudioErrorCode(err, 'STUDIO_HAS_UNAPPLIED_REQUEST')) {
            // Còn đề nghị nhập kim cương đã duyệt chưa ghi xong (S8): giữ modal, hiện câu của mã + lối sang
            // trang Đề nghị lọc theo Studio này.
            setDeleting({
              ...config,
              description: (
                <div>
                  {base}
                  <Alert
                    type="error"
                    showIcon
                    style={{ marginTop: 12 }}
                    message={mapStudioError(err)}
                    description={
                      <Link to={STUDIO_PAGES.listTab('requests', studio.id)}>
                        Mở đề nghị của Studio này
                      </Link>
                    }
                  />
                </div>
              ),
            });
          } else {
            notification.error({ message: mapStudioError(err) });
          }
          throw err;
        }
      },
    };
    setDeleting(config);
  };

  const invalidNotOps = rows.filter(
    (r) => r.manager && !r.manager.valid && r.manager.invalidReason === 'NOT_OPS_MANAGER'
  ).length;

  const columns: ColumnsType<StudioListItem> = [
    {
      title: 'Studio',
      key: 'studio',
      width: 240,
      fixed: 'left',
      render: (_, r) => (
        <div className={styles.person}>
          <Avatar size={36} src={imageUrl(r.avatar) || undefined} style={{ flex: 'none' }}>
            {initials(r.title)}
          </Avatar>
          <div className={styles.personText}>
            <Link to={STUDIO_PAGES.detail(r.id)} className={styles.personName}>
              {r.title}
            </Link>
            {r.tiktokGroupName && (
              <Tooltip title="Nhóm trong file TikTok">
                <span className={styles.personSub}>{r.tiktokGroupName}</span>
              </Tooltip>
            )}
          </div>
        </div>
      ),
    },
    {
      title: 'Quản lý',
      key: 'manager',
      width: 200,
      render: (_, r) => {
        if (!r.manager) return <span className={styles.muted}>{NO_MANAGER_TEXT}</span>;
        const reason = r.manager.invalidReason;
        return (
          <div>
            <div className={styles.cellTitle}>{r.manager.nickName}</div>
            {r.manager.username && <div className={styles.muted}>@{r.manager.username}</div>}
            {!r.manager.valid && (
              <Tooltip title={reason ? MANAGER_INVALID_TEXT[reason] : undefined}>
                <Tag color="red" style={{ marginTop: 2 }}>
                  {MANAGER_INVALID_TAG}
                </Tag>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: 'Creator',
      key: 'creators',
      width: 100,
      align: 'right',
      render: (_, r) => (
        <Tooltip title={creatorCountTooltip(r.counts)}>
          <span>{formatKc(r.counts.creators)}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Nhóm nhảy',
      key: 'teams',
      width: 100,
      align: 'right',
      render: (_, r) => formatKc(r.counts.teams),
    },
    {
      title: 'KC tháng này',
      key: 'kc',
      width: 170,
      align: 'right',
      render: (_, r) => {
        if (!r.kc) return '—';
        const sub = r.kc.shifted
          ? `Tháng ${monthNumber(r.kc.requestedMonth)} chưa có dữ liệu, đang hiện tháng ${monthLabel(
              r.kc.month
            )}`
          : asOfLabel(r.kc.asOfDay);
        return (
          <div>
            <NoWrap>{formatKc(r.kc.total)}</NoWrap>
            {sub && <div className={styles.muted}>{sub}</div>}
          </div>
        );
      },
    },
    {
      title: 'KC tháng trước',
      key: 'compare',
      width: 160,
      align: 'right',
      render: (_, r) =>
        r.kc ? (
          <div>
            <NoWrap>{formatKc(r.kc.compareTotal)}</NoWrap>
            <div className={styles.muted}>cả tháng {monthLabel(r.kc.compareMonth)}</div>
          </div>
        ) : (
          '—'
        ),
    },
    {
      title: 'Thay đổi (cùng số ngày)',
      key: 'delta',
      width: 260,
      render: (_, r) => {
        if (!r.kc) return '—';
        const same = r.kc.sameDay;
        return (
          <DeltaCell
            delta={same.delta}
            baseValue={same.baseValue}
            estimated={same.mode === 'ESTIMATE' || same.baseMode === 'ESTIMATE'}
            sub={same.label}
          />
        );
      },
    },
    {
      title: 'Đề nghị chờ',
      key: 'requests',
      width: 110,
      align: 'center',
      render: (_, r) => (
        <CountPill
          count={r.pending.requests}
          title="Đề nghị chờ Admin"
          onClick={
            r.pending.requests > 0
              ? () => navigate(STUDIO_PAGES.listTab('requests', r.id))
              : undefined
          }
        />
      ),
    },
    {
      title: 'Cờ mở',
      key: 'flags',
      width: 90,
      align: 'center',
      render: (_, r) => (
        <CountPill
          count={r.pending.openFlags}
          tone="accent"
          title="Cờ đang mở"
          onClick={
            r.pending.openFlags > 0
              ? () => navigate(STUDIO_PAGES.listTab('monitor', r.id))
              : undefined
          }
        />
      ),
    },
    {
      title: 'Thao tác',
      key: 'actions',
      fixed: 'right',
      width: viewer.canAdmin ? 190 : 80,
      render: (_, r) => (
        <Space size={4} wrap>
          <Link to={STUDIO_PAGES.detail(r.id)}>
            <Button size="small" icon={<EyeOutlined />}>
              Xem
            </Button>
          </Link>
          {viewer.canAdmin && (
            <>
              <Tooltip title="Sửa">
                <Button size="small" icon={<EditOutlined />} onClick={() => openModal('edit', r)} />
              </Tooltip>
              <Tooltip title="Đổi Quản lý">
                <Button
                  size="small"
                  icon={<SwapOutlined />}
                  onClick={() => openModal('manager', r)}
                />
              </Tooltip>
              <Tooltip title="Xoá">
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => openDelete(r)}
                />
              </Tooltip>
            </>
          )}
        </Space>
      ),
    },
  ];

  if (list.error && !list.data) {
    return <StudioErrorBlock error={list.error} onRetry={list.reload} />;
  }

  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <div className={styles.filters}>
          <Input.Search
            allowClear
            placeholder="Tên Studio hoặc Quản lý"
            style={{ width: 260 }}
            maxLength={100}
            onSearch={(value) => {
              setQ(value.trim());
              setPage(1);
            }}
          />
          <DatePicker
            picker="month"
            format="MM/YYYY"
            allowClear={false}
            value={dayjs(`${shownMonth}-01`)}
            disabledDate={(d) => d.format('YYYY-MM') > utcMonth()}
            onChange={(d) => {
              if (!d) return;
              setMonth(d.format('YYYY-MM'));
              setPage(1);
            }}
            aria-label="Tháng"
          />
          <Select<StudioSort>
            value={sort}
            options={STUDIO_SORT_OPTIONS}
            style={{ width: 220 }}
            onChange={(value) => {
              setSort(value);
              setPage(1);
            }}
            aria-label="Sắp xếp"
          />
        </div>
      </div>
      {list.error && <StudioErrorBlock error={list.error} onRetry={list.reload} />}
      {viewer.isAdmin && invalidNotOps > 0 && (
        <Alert type="warning" showIcon message={managerInvalidWarning(invalidNotOps)} />
      )}
      <div className="custom-table">
        <Table<StudioListItem>
          columns={columns}
          rowKey="id"
          dataSource={rows}
          loading={list.loading}
          pagination={false}
          locale={{ emptyText: viewer.canAdmin ? EMPTY_TEXT.studios : 'Chưa có Studio nào.' }}
          scroll={{ x: 1450 }}
        />
        {total > STUDIO_PAGE_LIMIT && (
          <Pagination
            showSizeChanger={false}
            showLessItems
            current={page}
            pageSize={STUDIO_PAGE_LIMIT}
            total={total}
            onChange={setPage}
          />
        )}
      </div>
      <StudioFormModal
        key={`edit-${modalSeq}`}
        open={modalOpen && modal?.kind === 'edit'}
        studio={modal?.kind === 'edit' ? modal.studio : null}
        onClose={() => setModalOpen(false)}
        onDone={afterChange}
      />
      <ChangeManagerModal
        key={`manager-${modalSeq}`}
        open={modalOpen && modal?.kind === 'manager'}
        studio={modal?.kind === 'manager' ? modal.studio : null}
        scopeMode={viewer.scopeMode}
        onClose={() => setModalOpen(false)}
        onDone={afterChange}
      />
      <NoteActionModal config={deleting} onClose={() => setDeleting(null)} />
    </div>
  );
};

export default StudioListTab;
